import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UsePendingMultiSelectionSimpleItems } from "@/common/composition/multi-selection-simple-items";
import { ContextLabel, InfoTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { clone, groupBy, uniq } from "@/fp";
import { cord3 } from "@/gql/lib";
import {
  AxisType,
  CoordinateInputTier3,
  Tier3AxisOperator,
  TimeAxisRangeInput,
} from "@/gql/types";
import { Ref, ref } from "@vue/composition-api";

export interface FiltersConfig {
  sensitivity: {
    include: boolean;
    selected: number[];
  };
  severities: {
    include: boolean;
    selected: number[];
  };
  sources: {
    include: boolean;
    selected: string[];
    only?: string[];
  };
  info_types: {
    include: boolean;
    selected: string[];
  };
  context_labels: {
    include: boolean;
    selected: string[];
    from?: string[];
  };
  insights: {
    include: boolean;
    selected: string[];
  };
}

const EMPTY_INFO_TYPE_SENTINEL = "__empty_info_type_selection__";

export const DEFAULT_FILTERS: FiltersConfig = {
  info_types: {
    include: true,
    selected: [],
  },
  context_labels: {
    include: false,
    selected: [],
  },
  sensitivity: {
    include: true,
    selected: [],
  },
  severities: {
    include: true,
    selected: [],
  },
  sources: {
    include: true,
    selected: [],
  },
  insights: {
    include: true,
    selected: [],
  },
};

const should_hide_info_types = (label_id: string) => {
  const label = SharedState.getInfoType(label_id);
  console.log("should_hide_info_types : ", label);
  if (label.labelType === "Virtual_label") {
    return false;
  }
  return (
    label.generation > 1 //|| label.parentId === InfoTypeHelper.REGULATED_ID()
  );
};

export const create_filters_config = (
  type: "events" | "files",
  cord: CoordinateInputTier3
) => {
  const { x, y } = cord;
  const filters: FiltersConfig = clone(DEFAULT_FILTERS, true);
  // data source
  if (x.type === AxisType.Application && x.values) {
    filters.sources.selected.push(x.values[0]);
  }
  if (y.type === AxisType.Application && y.values) {
    filters.sources.selected.push(y.values[0]);
  }
  if (SharedState.data_sources.value.length === 1) {
    filters.sources.include = false;
  }

  // content type
  if (x.type === AxisType.ContentLabel && x.values) {
    if (should_hide_info_types(x.values[0])) {
      filters.info_types.include = false;
    } else {
      if (SharedState.is_context_label(x.values[0])) {
        filters.context_labels.selected.push(x.values[0]);
      } else {
        filters.info_types.selected.push(x.values[0]);
      }
    }
  }
  if (y.type === AxisType.ContentLabel && y.values) {
    if (should_hide_info_types(y.values[0])) {
      filters.info_types.include = false;
    } else {
      if (SharedState.is_context_label(y.values[0])) {
        filters.context_labels.selected.push(y.values[0]);
      } else {
        filters.info_types.selected.push(y.values[0]);
      }
    }
  }

  // insights
  if (x.type === AxisType.Insight && x.values) {
    filters.insights.include = false;
    const ins = SharedState.insight_by_id(x.values[0]);
    filters.info_types.selected = ins ? [ins.infoTypeId] : [];
  }
  if (y.type === AxisType.Insight && y.values) {
    filters.insights.include = false;
    const ins = SharedState.insight_by_id(y.values[0]);
    filters.info_types.selected = ins ? [ins.infoTypeId] : [];
  }

  if (type === "files") {
    filters.insights.include = false;
  }

  if (type === "events") {

    // severities
    if (x.type === AxisType.Severity && x.values) {
      filters.severities.selected.push(+x.values[0]);
    }
    if (y.type === AxisType.Severity && y.values) {
      filters.severities.selected.push(+y.values[0]);
    }
    filters.sensitivity.include = false;
    filters.sensitivity.selected = [];
  } else {
    // sensitivities
    if (x.type === AxisType.Sensitivity && x.values) {
      filters.sensitivity.selected.push(+x.values[0]);
    }
    if (y.type === AxisType.Sensitivity && y.values) {
      filters.sensitivity.selected.push(+y.values[0]);
    }
    filters.severities.include = false;
    filters.severities.selected = [];
  }

  return filters;
};

export const UseFilters = (config: FiltersConfig) => {
  const sensitivities = UsePendingMultiSelectionSimpleItems({
    always_keep_one_on: false,
    items: [0, 1, 2, 3],
    selected: config.sensitivity.selected,
  });

  const severities = UsePendingMultiSelectionSimpleItems({
    always_keep_one_on: false,
    items: [1, 2, 3],
    selected: config.severities.selected,
  });

  const sources = UsePendingMultiSelectionSimpleItems({
    always_keep_one_on: false,
    items: SharedState.data_sources.value
      .filter((d) => {
        if (config.sources.only) {
          return config.sources.only.includes(d.id);
        } else {
          return true;
        }
      })
      .map((d) => d.id),
    selected: config.sources.selected,
  });
  const share_types: string[] = uniq(
    SharedState.compliance_insights.value.map((s) => s.shareType)
  );

  const ctx_labels = [
    ...SharedState.context_labels.value,
    ...SharedState.folders_context_labels.value.map((d) => {
      return {
        ...d,
        labelCategory: "Folders Information",
      };
    }),
  ];
  const relevant_context_labels = config.context_labels.from
    ? ctx_labels.filter((l) => config.context_labels.from?.includes(l.id))
    : ctx_labels;

  const f = groupBy<ContextLabel>(
    "labelCategory",
    // @ts-ignore
    relevant_context_labels.map((d) => {
      return {
        ...d,
        id: d.id,
        labelCategory: d.labelCategory,
        name: d.labelName,
      };
    })
  );

  // @ts-ignore
  const s = Array.from(f.entries()).reduce((acc: any[], [key, val]) => {
    return [...acc, { id: key, name: key, is_category: true }, ...val];
  }, []);

  const context_labels = UseDropdownItems([
    {
      id: "All",
      name: "All Contexts",
    },
    // @ts-ignore
    ...s,
  ]);
  // TODO:
  const insights = UseDropdownItems([
    {
      id: "All",
      name: "All Risk Types",
    },
    ...share_types.map((type) => {
      return {
        id: type,
        name: type === "anonymous" ? "Shared via anonymous link" : type,
      };
    }),
  ]);

  const selected_info_types: Ref<string[]> = ref([]);

  const get_selected_l0s_ids = () => {
    console.log("selected_info_types.value::", selected_info_types.value);
    const ids =
      config.info_types.include && selected_info_types.value.length > 0
        ? selected_info_types.value.map((d) => {
            const l0_code = SharedState.getInfoType(d).code;
            return SharedState.l0InfoTypes.value.find((d) => d.code === l0_code)
              ?.id as string;
          })
        : SharedState.l0InfoTypes.value.map((d) => d.id);
    return ids;
  };

  const to_cords = (z: TimeAxisRangeInput): CoordinateInputTier3[] => {
    const cords: CoordinateInputTier3[] = [];

    if (config.severities.include && severities.selected.value.length > 0) {
      cords.push({
        x: cord3(AxisType.None, []),
        y: {
          type: AxisType.Severity,
          values: severities.selected.value.map((val) => val.toString()),
        },
        z,
      });
    }

    if (config.sources.include && sources.selected.value.length > 0) {
      cords.push({
        x: cord3(AxisType.None, []),
        y: {
          type: AxisType.Application,
          values: sources.selected.value as string[],
        },
        z,
      });
    }

    if (config.sensitivity.include && sensitivities.selected.value.length > 0) {
      cords.push({
        x: cord3(AxisType.None, []),
        y: {
          type: AxisType.Sensitivity,
          values: sensitivities.selected.value.map((val) => val.toString()),
        },
        z,
      });
    }

    if (config.insights.include) {
      let insights_ids: string[] = [];
      const share_type = insights[1].value?.id as string;
      if (insights[1].value?.id !== "All") {
        const selected_severities =
          config.severities.include && severities.selected.value.length > 0
            ? severities.selected.value
            : [0, 1, 2, 3];
        const selected_l0s = get_selected_l0s_ids();
        console.log("selected_l0s::", selected_l0s);
        insights_ids = SharedState.compliance_insights.value
          .filter(
            (f) =>
              selected_severities.includes(f.severityLevel) &&
              f.shareType === share_type &&
              selected_l0s.includes(f.infoTypeId)
          )
          .map((d) => d.id);
      }
      if (insights_ids.length > 0) {
        cords.push({
          x: cord3(AxisType.None, []),
          y: {
            type: AxisType.Insight,
            values: insights_ids,
          },
          z,
        });
      }
    }
    const has_selected_info_type = selected_info_types.value.length > 0;
    const has_selected_context_label =
      config.context_labels.include && context_labels[1].value?.id !== "All";
    const should_return_no_results_for_cleared_initial_info_type =
      config.info_types.include &&
      config.info_types.selected.length > 0 &&
      !has_selected_info_type &&
      !has_selected_context_label;

    if (
      has_selected_info_type ||
      has_selected_context_label ||
      should_return_no_results_for_cleared_initial_info_type
    ) {
      const operator =
        has_selected_info_type && has_selected_context_label
          ? Tier3AxisOperator.And
          : Tier3AxisOperator.Or;
      const values = should_return_no_results_for_cleared_initial_info_type
        ? [EMPTY_INFO_TYPE_SENTINEL]
        : context_labels[1].value?.id === "All"
        ? selected_info_types.value
        : [context_labels[1].value?.id as string, ...selected_info_types.value];
      cords.push({
        x: cord3(AxisType.None, []),
        y: {
          type: AxisType.ContentLabel,
          values,
          operator,
        },
        z,
      });
    }
    return cords;
  };

  return {
    sensitivities,
    severities,
    sources,
    insights,
    context_labels,
    //info_types,
    selected_info_types,
    to_cords,
  };
};

/*   const info_types_col = UsePendingCollection({
    //seed: SharedState.get_raw_info_types_tree(),
    seed: clone(
      Object.values(SharedState.allInfoTypes.value).map((d) => {
        return {
          id: d.id,
          name: d.labelName || d.name,
          checked: false,
        };
      }),
      true
    ),
    uniq_prop: "id",
    // @ts-ignore
    check_change_by_prop: "checked",
  });
  const info_types = {
    ...info_types_col,
    check_only: (ids: string[]) => {
        const all_items = Object.values(info_types_col.get_indexed_pending_items);
        
    },
  }; */
